import * as React from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import propz from 'propz';
import { Autocomplete } from '../../../components/Autocomplete/Autocomplete';
import { DEFAULT_SKIP } from '../../../consts/table';
import { getName, getPostcode } from '../../../helpers/autocomplete/autocomplete';
import { getPhoneRegion } from '../../../helpers/region/region';
import { getSchoolPostcode, getSchoolPublic, getSchoolsPublic } from '../../../helpers/service/public/public';
import { getMainGeoSchoolFilterByParams } from '../../../helpers/venue/venue';

export type RegistrationSchoolRole = 'STAFF' | 'PARENT' | 'STUDENT';

interface Props {
  phone: string;
  role: RegistrationSchoolRole;
  onSubmit: (data: any) => void;
  onBack: (draft?: any) => void;
  initialSchool?: any;
  initialOneOffCode?: string;
}

const validationSchema = Yup.object().shape({
  schoolId: Yup.string().required('Choose a school to continue')
});

const ROLE_CONTENT = {
  STAFF: {
    title: 'Which school?',
    description: 'Search by name. A postcode narrows the list when several schools share a name.',
    schoolPlaceholder: 'Start typing a school name',
    submitLabel: 'Continue'
  },
  PARENT: {
    title: "Your child's school",
    description: "If the school already holds your details, we'll try to link you to your child automatically.",
    schoolPlaceholder: "Start typing your child's school",
    submitLabel: 'Continue'
  },
  STUDENT: {
    title: 'Your school',
    description: 'Search for the school that should review your student access request.',
    schoolPlaceholder: 'Start typing your school name',
    submitLabel: 'Review request'
  }
};

const normalisePostcode = (value: string) =>
  String(value || '')
    .replace(/\s+/g, '')
    .toUpperCase();

const escapeRegExp = (value: string) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function RegistrationSchoolPicker(props: Props) {
  const { phone, role, onSubmit, onBack, initialSchool, initialOneOffCode } = props;
  const content = ROLE_CONTENT[role];
  const region = getPhoneRegion(phone);
  const [postcode, setPostcode] = React.useState<any>(undefined);
  const [postcodeInputVersion, setPostcodeInputVersion] = React.useState(0);
  const [isResolvingSchool, setIsResolvingSchool] = React.useState(false);
  const [schoolLoadError, setSchoolLoadError] = React.useState('');
  const postcodeQueryRef = React.useRef('');

  const getPostcodes = React.useCallback(
    (text: string) => {
      const query = String(text || '').trim();
      if (normalisePostcode(query).length < 2) return Promise.resolve([]);

      return getSchoolPostcode({
        where: {
          region,
          postcode: {
            like: query,
            options: 'i'
          }
        },
        limit: 10,
        skip: DEFAULT_SKIP
      } as any);
    },
    [region]
  );

  const resolvePostcode = React.useCallback(async () => {
    const query = postcodeQueryRef.current.trim();
    const selectedPostcode = postcode ? getPostcode(postcode) : '';

    if (postcode && (!query || normalisePostcode(query) === normalisePostcode(selectedPostcode))) {
      return postcode;
    }

    // A full UK/US postcode is at least five characters without spaces. For shorter
    // fragments the user must choose a suggestion so that we do not sort around an
    // arbitrary postcode that merely shares the same prefix.
    if (normalisePostcode(query).length < 5) return undefined;

    const matches = await getPostcodes(query);
    return matches[0];
  }, [getPostcodes, postcode]);

  const getSchools = React.useCallback(
    async (text: string) => {
      const schoolName = String(text || '').trim();
      const locationPostcode = await resolvePostcode();
      const point = propz.get(locationPostcode, ['point']);

      if (schoolName.length < 2 && typeof point === 'undefined') return [];

      const where: any = {
        region,
        kind: { $in: role === 'STAFF' ? ['School', 'SchoolUnion'] : ['School'] },
        availableForRegistration: { $ne: false }
      };

      if (role === 'STAFF') {
        where.$or = [
          { 'allowedPermissionPresets.ADMIN': true },
          { 'allowedPermissionPresets.MANAGER': true },
          { 'allowedPermissionPresets.TEACHER': true },
          { 'allowedPermissionPresets.COACH': true }
        ];
      } else if (role === 'PARENT') {
        where.allowedPermissionPresets = { PARENT: true };
      } else {
        where.allowedPermissionPresets = { STUDENT: true };
        where.studentSelfRegistrationEnabled = { $ne: false };
      }

      if (schoolName.length >= 2) {
        where.name = {
          like: escapeRegExp(schoolName),
          options: 'i'
        };
      }

      const filter: any = {
        view: { type: 'id_name_pic' },
        where,
        limit: 10,
        skip: DEFAULT_SKIP
      };

      if (typeof point !== 'undefined') {
        filter.where['postcode.point'] = getMainGeoSchoolFilterByParams(0, point);
      } else {
        filter.order = 'name ASC';
      }

      return getSchoolsPublic(filter);
    },
    [region, resolvePostcode, role]
  );

  const renderSchoolAccountState = React.useCallback(
    (school: any) => {
      const hasStaff = propz.get(school, ['stats', 'rolesExistence', 'staff']);
      if (typeof hasStaff !== 'boolean' || propz.get(school, ['kind']) === 'SchoolUnion') return null;

      if (hasStaff) {
        return <span className="eRegistrationSchoolState isExisting">Already on Squad In Touch</span>;
      }

      return (
        <span className="eRegistrationSchoolState isNew">
          {role === 'STAFF' ? 'No account yet' : 'School account not active yet'}
        </span>
      );
    },
    [role]
  );

  return (
    <div className="eRegistrationSchoolPicker">
      <div className="eSchoolSignupScreenHeader">
        <h1>{content.title}</h1>
        <p>{content.description}</p>
      </div>

      {role === 'STUDENT' && (
        <div className="eRegistrationSchoolNotice">
          Most pupils already have an account created by their school. Check with your school before requesting a new
          one.
        </div>
      )}

      <Formik
        initialValues={{
          schoolId: initialSchool ? initialSchool.id : '',
          school: initialSchool,
          oneOffCode: initialOneOffCode || ''
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ touched, errors, setFieldValue, values }) => (
          <Form>
            <div className="eRegistrationSchoolSearchGrid">
              <div className="form-group eRegistrationSchoolNameField">
                <label>School</label>
                <Field name="schoolId">
                  {() => (
                    <Autocomplete
                      searchFunction={getSchools}
                      getElementTitle={getName}
                      customClass="mFullWidth"
                      defaultItem={values.school}
                      onInputChange={value => {
                        if (values.school && value !== getName(values.school)) {
                          setFieldValue('school', undefined);
                          setFieldValue('schoolId', '');
                          setFieldValue('oneOffCode', '');
                        }
                        setSchoolLoadError('');
                      }}
                      onSelect={async schoolMinView => {
                        setIsResolvingSchool(true);
                        setSchoolLoadError('');
                        try {
                          const school = await getSchoolPublic(schoolMinView.id);
                          setFieldValue('school', school);
                          setFieldValue('schoolId', school.id);
                          setFieldValue('oneOffCode', '');
                        } catch (error) {
                          setFieldValue('school', undefined);
                          setFieldValue('schoolId', '');
                          setSchoolLoadError('We could not load this school. Please try again.');
                        } finally {
                          setIsResolvingSchool(false);
                        }
                      }}
                      placeholder={content.schoolPlaceholder}
                      emptyMessage="Enter at least two characters of a school name, or choose a postcode to see nearby schools."
                      isItemWithImage={true}
                      renderItemMeta={renderSchoolAccountState}
                    />
                  )}
                </Field>
              </div>

              <div className="form-group eRegistrationPostcodeField">
                <label>
                  Postcode <span>optional</span>
                </label>
                <div className="eRegistrationLookupGrid">
                  <Autocomplete
                    key={`registration-postcode-${postcodeInputVersion}`}
                    searchFunction={getPostcodes}
                    getElementTitle={getPostcode}
                    customClass="mFullWidth"
                    defaultItem={postcode}
                    onInputChange={value => {
                      postcodeQueryRef.current = value;
                    }}
                    onSelect={postcodeNext => {
                      postcodeQueryRef.current = getPostcode(postcodeNext);
                      setPostcode(postcodeNext);
                      setFieldValue('school', undefined);
                      setFieldValue('schoolId', '');
                      setFieldValue('oneOffCode', '');
                    }}
                    placeholder="Home or school postcode"
                    emptyMessage="Enter at least two characters, then choose a suggested postcode."
                  />
                  <button
                    type="button"
                    className="eRegistrationClearButton"
                    aria-label="Clear postcode"
                    title="Clear postcode"
                    onClick={() => {
                      postcodeQueryRef.current = '';
                      setPostcode(undefined);
                      setPostcodeInputVersion(version => version + 1);
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            <div className="eRegistrationSchoolSearchHint">
              Enter your home or school postcode and/or part of the school name. Matching schools are shown by name;
              with a postcode, the nearest matches come first.
            </div>

            {touched.schoolId && errors.schoolId && (
              <div className="eRegistrationInlineError">{String(errors.schoolId)}</div>
            )}
            {schoolLoadError && <div className="eRegistrationInlineError">{schoolLoadError}</div>}

            {role === 'STUDENT' && values.school && values.school.isOneOffCodeOnRegisterEnabled && (
              <div className="form-group eRegistrationSchoolCodeField">
                <label>
                  Code from your school <span>optional</span>
                </label>
                <Field name="oneOffCode" className="form-control" placeholder="e.g. RH-4821" />
                <span className="eRegistrationFieldHint">
                  With a code, approval can be immediate. Without one, a member of staff checks your request.
                </span>
              </div>
            )}

            <div className="eRegistrationFormActions">
              <button type="button" className="btn btn-secondary" onClick={() => onBack(values)}>
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary eRegistrationContinueButton"
                disabled={isResolvingSchool}
              >
                {isResolvingSchool ? 'Loading school…' : content.submitLabel}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
